// Quellen für den Activity Feed. Bewusst fünf schmale Index-Queries statt eines
// Fan-out-Lambdas — Zusammenführen passiert im Frontend (siehe ActivityFeed.js).
// Feldnamen und Argumenttypen sind gegen das kompilierte Schema geprüft:
// Orders nutzt companyId (kleines d) und hat keinen Datums-Index, daher
// orderNumber als Sortierschlüssel und Zeitsortierung beim Merge.

export const communicationMessagesByCompanyID = /* GraphQL */ `
  query CommunicationMessagesByCompanyID(
    $companyID: ID!
    $limit: Int
    $nextToken: String
  ) {
    communicationMessagesByCompanyID(
      companyID: $companyID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        createdAt
        updatedAt
        editedAt
        content
        broadcastID
        objectID
        objectType
        countries
        userID
        user {
          firstName
          lastName
        }
        messageUser {
          firstName
          lastName
        }
        companyID
        taskStatus
        contactPerson {
          firstName
          lastName
        }
        contactPersonUserId
        mentionedUsers {
          firstName
          lastName
        }
        documents {
          items {
            id
            name
            fileType
            storageUrl
            type
            companyId
            countries
            createdAt
            updatedAt
          }
        }
        reactions {
          reaction
          users {
            firstName
            lastName
          }
        }
        readReceipts {
          userId
          firstName
          lastName
          deliveredAt
          readAt
        }
        quotedMessageID
        quotedMessage {
          id
          content
          createdAt
          messageUser {
            firstName
            lastName
          }
          companyID
          countries
        }
      }
      nextToken
    }
  }
`;

// Feldauswahl der Aufgaben, von beiden Task-Quellen benutzt (Firma und Kontakt).
// Eine Stelle: die Kommentare darin erklaeren, warum jedes Feld mitmuss — zweimal
// gepflegt waere genau das, was beim naechsten Feld auseinanderlaeuft.
const TASK_FIELDS = /* GraphQL */ `
        id
        createdAt
        subject
        # A-I Punkt 4: gekuerzt unter dem Betreff. Ohne body steht im Verlauf nur
        # "Rueckruf" — das Ergebnis des Telefonats war nicht lesbar.
        body
        taskType
        status
        dueDate
        completedAt
        reminderMinutesBefore
        # E-V / G3: die Gegenseite. Ist die Aufgabe zu einer Aktivitaet geworden,
        # zeigt der Verlauf die Tatsache und nicht die Absicht — aber nur, solange
        # deren Quelle eingeschaltet ist. Beim Loeschen wird hierueber der Rueckzeiger
        # Activities.taskID geloest (helpers/tasks.js).
        activityIDs
        originActivityID
        # Der Bearbeiten-Knopf am Eintrag belegt das Formular hiermit vor. Sie MUESSEN
        # mit: updateTasks schickt geleerte Felder als null, ein hier nicht gelesenes
        # Feld waere nach dem Speichern weg.
        priority
        assignedUserId
        companyID
        contactPersonId
        # Kein contactPerson-Block: Tasks hat keine @hasOne-Verbindungen (Schema,
        # Ressourcenlimit). Der Verlauf traegt ihn ueber hydrateTasks nach.
        # Anhaenge, denormalisiert am Datensatz (Documents ohne @hasMany, siehe Schema).
        documents {
          id
          name
          fileType
          type
        }
        objectID
        objectType
        associatedObjectIds
        associatedObjects
`;

export const tasksByCompanyIdByDueDate = /* GraphQL */ `
  query TasksByCompanyIdByDueDate($companyID: ID!, $limit: Int, $nextToken: String) {
    tasksByCompanyIdByDueDate(
      companyID: $companyID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ${TASK_FIELDS}
      }
      nextToken
    }
  }
`;

// Aufgaben eines Kontakts, unabhaengig von der Firma. Der GSI existiert genau
// dafuer (Schema: byContactPersonIdByDueDate) — vorher lief die Kontakt-Ansicht
// ueber den Firmen-Index und filterte im Frontend nach, was bei seitenweisen
// Cursorn aus einer Seite von 25 Eintraegen drei machte. Fuer Kontakte ohne Firma
// ist es ausserdem der einzige Weg: die tragen keine companyID.
export const tasksByContactPersonIdByDueDate = /* GraphQL */ `
  query TasksByContactPersonIdByDueDate(
    $contactPersonId: ID!
    $limit: Int
    $nextToken: String
  ) {
    tasksByContactPersonIdByDueDate(
      contactPersonId: $contactPersonId
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ${TASK_FIELDS}
      }
      nextToken
    }
  }
`;

export const customerRequestsByCompanyID = /* GraphQL */ `
  query CustomerRequestsByCompanyID($companyID: ID!, $limit: Int, $nextToken: String) {
    customerRequestsByComanyIdByCreatedAt(
      companyID: $companyID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        createdAt
        number
        currentStatus
      }
      nextToken
    }
  }
`;

export const salesOffersByCompanyID = /* GraphQL */ `
  query SalesOffersByCompanyID($companyID: ID!, $limit: Int, $nextToken: String) {
    salesOffersByCompanyID(
      companyID: $companyID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        createdAt
        number
        status
        customerRequestID
        customerRequest {
          id
          number
        }
      }
      nextToken
    }
  }
`;

// ── Anfrage-Ebene (Anforderung R5) ─────────────────────────────────────────
// Andere Indizes als auf Firmenebene:

// Notizen und Kommentare an der Anfrage — eigener Index, kein Filtern nötig.
export const communicationMessagesByObjectId = /* GraphQL */ `
  query CommunicationMessagesByObjectId($objectID: ID!, $limit: Int, $nextToken: String) {
    communicationMessagesByObjectId(
      objectID: $objectID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        createdAt
        updatedAt
        editedAt
        content
        broadcastID
        objectID
        objectType
        countries
        userID
        user {
          firstName
          lastName
        }
        messageUser {
          firstName
          lastName
        }
        companyID
        taskStatus
        contactPerson {
          firstName
          lastName
        }
        contactPersonUserId
        mentionedUsers {
          firstName
          lastName
        }
        documents {
          items {
            id
            name
            fileType
            storageUrl
            type
            companyId
            countries
            createdAt
            updatedAt
          }
        }
        reactions {
          reaction
          users {
            firstName
            lastName
          }
        }
        readReceipts {
          userId
          firstName
          lastName
          deliveredAt
          readAt
        }
        quotedMessageID
        quotedMessage {
          id
          content
          createdAt
          messageUser {
            firstName
            lastName
          }
          companyID
          countries
        }
      }
      nextToken
    }
  }
`;

// Angebote der Anfrage. SalesOffer.customerRequestID hat einen @index OHNE queryField,
// es gibt also keine Top-Level-Query — erreichbar nur über die @hasMany-Verbindung
// der Anfrage selbst.
export const customerRequestActivity = /* GraphQL */ `
  query CustomerRequestActivity($id: ID!) {
    getCustomerRequests(id: $id) {
      id
      number
      salesOffers {
        items {
          id
          createdAt
          number
          status
          customerRequestID
        }
      }
    }
  }
`;

// Dokumente an der Anfrage (Angebots-PDF, Anhänge).
export const documentsByObjectId = /* GraphQL */ `
  query DocumentsByObjectId($objectID: ID!, $limit: Int, $nextToken: String) {
    documentsByObjectId(
      objectID: $objectID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        createdAt
        name
        type
      }
      nextToken
    }
  }
`;

export const ordersByCompanyID = /* GraphQL */ `
  query OrdersByCompanyID($companyId: ID!, $limit: Int, $nextToken: String) {
    ordersByCompanyIdByOrderNumber(
      companyId: $companyId
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        createdAt
        orderNumber
        currentOrderStatus
      }
      nextToken
    }
  }
`;

// E-V: Activities als Feed-Quelle. Drei Quellen statt einer, weil E-V zwei getrennte
// Checkboxen verlangt ("Anrufe", "Termine") und E-VI die Notizen dazustellt — der
// Filter sitzt auf dem Index, nicht im Frontend, damit jede Quelle ihren eigenen
// Cursor behaelt und "Mehr anzeigen" auch im gefilterten Zustand nachlaedt.
//
// KEINE Backticks in den Kommentaren hier drin: die Query ist ein Template-Literal,
// ein Backtick beendet es und der Build bricht mit "Missing semicolon" ab.
export const activitiesByCompanyID = /* GraphQL */ `
  query ActivitiesByCompanyID(
    $companyID: ID!
    $activityType: String!
    $limit: Int
    $nextToken: String
  ) {
    activitiesByCompanyIdByOccurredAt(
      companyID: $companyID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
      filter: { activityType: { eq: $activityType } }
    ) {
      items {
        id
        activityType
        subject
        body
        occurredAt
        direction
        durationSeconds
        outcome
        location
        endsAt
        objectID
        objectType
        taskID
        userId
        contactPersonId
        phoneNumber
        companyID
        # Anhaenge, denormalisiert am Datensatz (Documents ohne @hasMany, siehe Schema).
        documents {
          id
          name
          fileType
          type
        }
      }
      nextToken
    }
  }
`;

export const activitiesByContactPersonID = /* GraphQL */ `
  query ActivitiesByContactPersonID(
    $contactPersonId: ID!
    $activityType: String!
    $limit: Int
    $nextToken: String
  ) {
    activitiesByContactPersonIdByOccurredAt(
      contactPersonId: $contactPersonId
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
      filter: { activityType: { eq: $activityType } }
    ) {
      items {
        id
        activityType
        subject
        body
        occurredAt
        direction
        durationSeconds
        outcome
        location
        endsAt
        objectID
        objectType
        taskID
        userId
        contactPersonId
        phoneNumber
        companyID
        # Anhaenge, denormalisiert am Datensatz (Documents ohne @hasMany, siehe Schema).
        documents {
          id
          name
          fileType
          type
        }
      }
      nextToken
    }
  }
`;

export const customListContactPersons = /* GraphQL */ `
  query CustomListContactPersons($type: String, $country: String) {
    customListContactPersons(type: $type, country: $country) {
      id
      email
      firstName
      lastName
    }
  }
`;

// Zentrale + Name der Firma fuer das Anruf-Fenster. Die Anfrage-Seite reicht nur
// `{ id }` herein — ohne diese Abfrage bliebe dort der Abschnitt "Company number" leer.
export const getCompanyCallInfo = /* GraphQL */ `
  query GetCompanyCallInfo($id: ID!) {
    getCompanies(id: $id) {
      id
      name
      number
      type
      phone
      doNotContact
    }
  }
`;

export const companiesContactPersonsByCompanyID = /* GraphQL */ `
  query ContactsByCompany($companyID: ID!, $nextToken: String) {
    companiesContactPersonsByCompanyID(
      companyID: $companyID
      limit: 200
      nextToken: $nextToken
    ) {
      items {
        id
        firstName
        lastName
        email
        phone
        mobil
        position
        # C-II: die drei Anrede-Platzhalter. Ohne sie bleiben {{salutation}},
        # {{formalGreeting}} und {{personalGreeting}} im Schreibfenster dauerhaft
        # leer — und der Versand bleibt wegen der Luecke gesperrt.
        salutation
        gender
        personalSalutation
      }
      nextToken
    }
  }
`;

// B-I: Mail als Feed-Quelle. Ein Eintrag je Mail, keine Gruppierung nach Konversation.
//
// `deletedAt` wird mitgelesen, nicht wegen der Anzeige, sondern wegen des Grabsteins aus
// B-VII: eine geloeschte Mail behaelt ihren Datensatz, damit der Sync sie nicht beim
// naechsten Lauf erneut anlegt. Sie gehoert nicht in den Verlauf.
export const emailsByCompanyID = /* GraphQL */ `
  query EmailsByCompanyID($companyID: ID!, $limit: Int, $nextToken: String) {
    emailsByCompanyIdByReceivedAt(
      companyID: $companyID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        direction
        subject
        bodyPreview
        bodyText
        receivedAt
        fromName
        fromEmail
        toEmails
        ccEmails
        hasAttachments
        webLink
        userId
        deletedAt
        taskID
        # B-I: nur ein Kennzeichen, kein Inhalt — "Vollstaendig anzeigen" holt die
        # formatierte Fassung live aus dem Postfach und braucht dafuer eine
        # Graph-Nachricht. Eine migrierte Mail (E-VII) hat keine; ohne dieses Feld
        # stuende der Knopf an 19.000 Zeilen, hinter denen nichts liegt.
        graphMessageId
        # B-VII Teil 1: die aktuelle Zuordnung, damit das Zuordnungs-Fenster sie
        # vorbelegen kann. Die Aktion reassign setzt contactPersonID auf genau den Wert,
        # den sie mitbekommt — ohne diese Auswahl waere er beim Speichern immer leer und
        # die bestehende Kontaktzuordnung stillschweigend weg.
        # (KEINE Backticks in diesen Kommentaren: die Query ist ein Template-Literal,
        # ein Backtick beendet es und der Build bricht mit "Missing semicolon" ab.)
        contactPersonID
      }
      nextToken
    }
  }
`;

export const emailsByContactPersonID = /* GraphQL */ `
  query EmailsByContactPersonID($contactPersonID: ID!, $limit: Int, $nextToken: String) {
    emailsByContactPersonIdByReceivedAt(
      contactPersonID: $contactPersonID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        direction
        subject
        bodyPreview
        bodyText
        receivedAt
        fromName
        fromEmail
        toEmails
        ccEmails
        hasAttachments
        webLink
        userId
        deletedAt
        taskID
        # B-I: nur ein Kennzeichen, kein Inhalt — "Vollstaendig anzeigen" holt die
        # formatierte Fassung live aus dem Postfach und braucht dafuer eine
        # Graph-Nachricht. Eine migrierte Mail (E-VII) hat keine; ohne dieses Feld
        # stuende der Knopf an 19.000 Zeilen, hinter denen nichts liegt.
        graphMessageId
        # B-VII Teil 1: die aktuelle Zuordnung, damit das Zuordnungs-Fenster sie
        # vorbelegen kann. Die Aktion reassign setzt contactPersonID auf genau den Wert,
        # den sie mitbekommt — ohne diese Auswahl waere er beim Speichern immer leer und
        # die bestehende Kontaktzuordnung stillschweigend weg.
        # (KEINE Backticks in diesen Kommentaren: die Query ist ein Template-Literal,
        # ein Backtick beendet es und der Build bricht mit "Missing semicolon" ab.)
        contactPersonID
      }
      nextToken
    }
  }
`;

// Anfrage-Ebene, zwei Schritte. `EmailLinks` traegt nur `emailID` und KEINE Relation
// auf `Emails` — die Mails werden deshalb mit EINEM Dokument aus Aliassen nachgeladen,
// dasselbe Muster wie die Firmennamen im Kanban (dort gemessen: 47 Datensaetze in
// 294 ms). Zwei Runden statt einer Verschachtelung, dafuer keine Schemaaenderung.
export const emailLinksByObjectId = /* GraphQL */ `
  query EmailLinksByObjectId($objectID: ID!, $limit: Int, $nextToken: String) {
    emailLinksByObjectId(
      objectID: $objectID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        emailID
        receivedAt
      }
      nextToken
    }
  }
`;

// Ein Dokument, ein Alias je Mail. Leere Liste -> null, damit der Aufrufer gar nicht
// erst abfragt.
export const emailsByIdsDocument = (ids) =>
  ids.length
    ? `query EmailsByIds {
${ids
  .map(
    (id, index) => `  e${index}: getEmails(id: ${JSON.stringify(id)}) {
    id
    direction
    subject
    bodyPreview
    bodyText
    receivedAt
    fromName
    fromEmail
    toEmails
    ccEmails
    hasAttachments
    webLink
    userId
    deletedAt
    taskID
    graphMessageId
    contactPersonID
  }`
  )
  .join("\n")}
}`
    : null;

// Eine Mail-Aufgabe zeigt mit `Tasks.activityIDs` auf ihre Mail(s) (Schema: "die
// Gegenrichtung zu Activities.taskID bzw. Emails.taskID"). Der Aufgabentyp sagt, in
// welcher Tabelle nachgesehen wird — hier also `Emails`, per ID und nicht ueber einen
// Abzug. Nur die Kopfdaten: den Text holt sich das Lesefenster live aus dem Postfach.
export const getEmailForView = /* GraphQL */ `
  query GetEmailForView($id: ID!) {
    getEmails(id: $id) {
      id
      subject
      fromName
      fromEmail
      toEmails
      ccEmails
      hasAttachments
      # Kopfzeile des Lesefensters: Datum und Richtung. Ueber den Verlauf liegen sie
      # in der Zeile schon vor, ueber die Mail-Aufgabe kommen sie nur von hier.
      receivedAt
      direction
      graphMessageId
      bodyText
      bodyPreview
    }
  }
`;

// Artikel der Firma. ItemsHead, nicht Items — die Firmenseite (ItemsTab) zeigt
// ebenfalls die Kopfsaetze, und /item/:id erwartet deren ID. Der Titel steckt in
// den Uebersetzungen, deshalb kommen sie mit.
export const itemsHeadByCompanyID = /* GraphQL */ `
  query ItemsHeadByCompanyID($companyID: ID!, $limit: Int, $nextToken: String) {
    itemsHeadByCompanyID(
      companyID: $companyID
      sortDirection: DESC
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        createdAt
        translations {
          items {
            languageCode
            field
            translation
          }
        }
      }
      nextToken
    }
  }
`;
